'use client'

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'

import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles'

import { darkTheme, lightTheme } from '@/styles/theme/mui-theme'

type Theme = 'light' | 'dark' | 'system'

interface ThemeContextType {
  theme: Theme
  actualTheme: 'light' | 'dark'
  setTheme: (theme: Theme) => void
  isDarkMode: boolean
  toggleTheme: () => void
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

export const useTheme = () => {
  const context = useContext(ThemeContext)
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }
  return context
}

interface ThemeProviderProps {
  children: React.ReactNode
}

export function ThemeProvider({ children }: ThemeProviderProps) {
  // Default to light theme, but allow manual switching
  const [theme, setTheme] = useState<Theme>('light')
  const [actualTheme, setActualTheme] = useState<'light' | 'dark'>('light')

  // Update actual theme when theme preference changes
  // System detection disabled - only manual switching allowed
  useEffect(() => {
    switch (theme) {
      case 'light':
        setActualTheme('light')
        break
      case 'dark':
        setActualTheme('dark')
        break
      case 'system':
        // If system is selected, default to light (not system preference)
        setActualTheme('light')
        break
    }
  }, [theme])

  // Load saved theme or default to light
  useEffect(() => {
    const savedTheme = localStorage.getItem('theme') as Theme
    // Only use saved theme if it's 'light' or 'dark'
    // Never use 'system' - default to light instead
    if (savedTheme === 'dark') {
      setTheme('dark')
      setActualTheme('dark')
    } else if (savedTheme === 'light') {
      setTheme('light')
      setActualTheme('light')
    } else {
      // Default to light for any other value including 'system' or no saved value
      setTheme('light')
      setActualTheme('light')
      localStorage.setItem('theme', 'light')
    }
  }, [])

  // Update localStorage and HTML data-theme attribute
  useEffect(() => {
    localStorage.setItem('theme', theme)
    // Update HTML element for CSS styling
    if (typeof document !== 'undefined') {
      document.documentElement.setAttribute('data-theme', actualTheme)
    }
  }, [theme, actualTheme])

  const toggleTheme = useCallback(() => {
    // Toggle between light and dark (no system option)
    setTheme((prev) => {
      const newTheme = prev === 'dark' ? 'light' : 'dark'
      setActualTheme(newTheme)
      return newTheme
    })
  }, [])

  // Safe setTheme that prevents system theme - memoized to prevent context recreation
  const safeSetTheme = useCallback((newTheme: Theme) => {
    if (newTheme === 'system') {
      setTheme('light')
      setActualTheme('light')
    } else {
      setTheme(newTheme)
      setActualTheme(newTheme as 'light' | 'dark')
    }
  }, [])

  const contextValue = useMemo<ThemeContextType>(
    () => ({
      theme,
      actualTheme,
      setTheme: safeSetTheme,
      isDarkMode: actualTheme === 'dark',
      toggleTheme,
    }),
    [theme, actualTheme, safeSetTheme, toggleTheme]
  )

  // Use the appropriate theme based on actualTheme - memoized to prevent MUI tree rerenders
  const muiTheme = useMemo(
    () => (actualTheme === 'dark' ? darkTheme : lightTheme),
    [actualTheme]
  )

  return (
    <ThemeContext.Provider value={contextValue}>
      <MuiThemeProvider theme={muiTheme}>{children}</MuiThemeProvider>
    </ThemeContext.Provider>
  )
}
